import { FC, useEffect, useState, useCallback } from 'react';
import { FormProvider, useForm } from "react-hook-form";
import { Autocomplete, TextField, Tooltip, InputAdornment, IconButton } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { makeStyles } from "@material-ui/core/styles";
import { SearchIcon } from 'lucide-react';
import axios from '../../utils/axios';

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
});

interface FilterProps {
  type: string;
  filterHandler: (data: any) => void;
}

//Employee filters component.
const EmployeeFilters: FC<FilterProps> = ({ type, filterHandler }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [filterQuery, setFilterQuery] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [newOption, setNewOption] = useState("");
  const [closedSelect, setClosedSelect] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  // use form
  const methods = useForm<any>({});

  const createFilter = useCallback(() => {
    let filter = (filterQuery && filterQuery !== '') ? `filter=${filterQuery}` : '';
    let employeeIdFilter = (selectedEmployeeId && selectedEmployeeId !== '') ? `&employeeId=${selectedEmployeeId}` : '';
    let queryString = `${filter}${employeeIdFilter}`;
    filterHandler(queryString);
  }, [filterQuery, selectedEmployeeId, filterHandler]);

  const onResetClickHandler = () => {
    setFilterQuery('');
    setSelected(null);
    setSelectedEmployeeId('');
    setNewOption('');
    setClosedSelect(true);

    // Trigger filter creation with empty values
    setTimeout(() => {
      filterHandler('');
    }, 100);
  };

  const searchEmployees = async (searchValue: string) => {
    setFilterQuery(searchValue);
  }

  useEffect(() => {
    // Only create filter when at least one filter parameter is set
    if (filterQuery || selectedEmployeeId) {
      createFilter();
    }
  }, [filterQuery, selectedEmployeeId, createFilter]);

  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setSelected(null);
    setSelectedEmployeeId('');
    setNewOption(value);

    // Search employees by name using the backend API
    if (value && value.length >= 1 && selectedAccount && selectedAccount._id) {
      try {
        const accountId = selectedAccount._id;

        // Call the employee search API - search by name, email, or employee ID
        const response = await axios.get(`/api/v1/employee/account/${accountId}/emp-list?filter=${value}&limit=10`);

        if (response.data && response.data.employees) {
          setOptions(response.data.employees);
        }
      } catch (error) {
        console.error('Error searching employees:', error);
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  };

  const onChangeHandler = (event: any, value: any) => {
    setNewOption('');
    if (value) {
      setSelectedEmployeeId(value._id);
      setSelected(value);
    } else {
      setSelectedEmployeeId('');
      setSelected(null);
    }
  };

  const onCloseHandler = () => {
    setClosedSelect(true);
  };

  const onOpenHandler = () => {
    setClosedSelect(false);
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={'Reset Filter'}>
          <IconButton onClick={() => { onResetClickHandler(); }}
            style={{ marginRight: '15px', marginLeft: '5px', color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1' }}
            size='small' ><Refresh /></IconButton>
        </Tooltip>
        <FormProvider {...methods}>
          <TextField className='c-text-field-3'
            variant="standard"
            style={{ width: 300 }}
            size='small'
            value={filterQuery}
            placeholder='Search Name, Email, Employee ID ...'
            onChange={(e) => searchEmployees(e.target.value.toString())}
            InputProps={{
              disableUnderline: true,
              startAdornment: <InputAdornment position="start"><SearchIcon style={{ marginTop: -3 }} size={20} /></InputAdornment>,
              style: {
                fontSize: 14,
                fontWeight: 'normal',
              },
            }}
          />

          <Autocomplete
            disablePortal
            id={`Search Employee`}
            value={selected || null}
            style={{ width: 300, float: 'left', marginLeft: '10px' }}
            onChange={onChangeHandler}
            onInputChange={onInputChangeHandler}
            onClose={onCloseHandler}
            onOpen={onOpenHandler}
            options={options}
            classes={{ paper: classes.paper }}
            getOptionLabel={option => option.name || ''}
            renderInput={(params) => <TextField {...params}
              className='c-text-field-3'
              variant="standard"
              name="search"
              size='small'
              placeholder={`Select Employee`}
              InputProps={{ ...params.InputProps, disableUnderline: true }}
            />}
          />
        </FormProvider>
      </div>
    </>
  );
}

export default EmployeeFilters;
